import { useState } from 'react';
import type { PublicFnbResponse } from '@nexacore/api-clients/fnb/public';
import { usableMediaImage, type PublicProduct } from '@/features/menu/types';
import type { PublicEvent } from './discoveryApi';
import { EventCarousel } from './EventCarousel';

type PublicStore = NonNullable<NonNullable<PublicFnbResponse['data']>['session']>['store'];

export function StorefrontHero({
  store,
  products,
  events = [],
}: {
  store: PublicStore | null;
  products: readonly PublicProduct[];
  events?: readonly PublicEvent[];
}) {
  const [musicPlaying, setMusicPlaying] = useState(false);
  const image = products
    .flatMap((product) => product.media ?? [])
    .sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)))
    .map((media) => usableMediaImage(media))
    .find(Boolean);

  if (events.length) {
    return (
      <section className="storefront-hero storefront-event-hero" aria-label="Sự kiện cửa hàng">
        <EventCarousel events={[...events]} />
      </section>
    );
  }

  return (
    <section className="storefront-hero" aria-labelledby="storefront-hero-heading">
      <div className="hero-copy">
        <p className="storefront-section-kicker">Dream Up · Premium Cacao</p>
        <h1 id="storefront-hero-heading">
          Dám mơ lại
          <span>Dám bắt đầu</span>
        </h1>
        <p className="hero-lead">
          Một ly cacao, một khoảng nghỉ và một lời nhắn tiếp thêm cảm hứng cho ngày của bạn.
        </p>
        <a className="hero-order-action" href="#menu">
          Order ngay <span aria-hidden="true">→</span>
        </a>
        <button
          className="hero-music-card"
          type="button"
          aria-pressed={musicPlaying}
          onClick={() => setMusicPlaying((playing) => !playing)}
        >
          <span className="music-control" aria-hidden="true">
            {musicPlaying ? 'Ⅱ' : '▶'}
          </span>
          <span>
            <strong>{musicPlaying ? 'Đã chọn chế độ thư giãn' : 'Nhạc thư giãn'}</strong>
            <small>Không tự động phát âm thanh</small>
          </span>
        </button>
      </div>

      <div className="hero-visual" aria-label={`Hương vị cacao tại ${store?.name ?? 'Dream Up'}`}>
        {image ? (
          <img
            src={image}
            width="720"
            height="720"
            alt="Ly cacao nổi bật"
            loading="eager"
            decoding="async"
            sizes="(max-width: 720px) 92vw, 600px"
          />
        ) : (
          <div className="hero-cup-fallback" role="img" aria-label="Minh họa ly cacao">
            <span>Dream Up</span>
          </div>
        )}
        <div className="hero-quality-badge" aria-label="100 phần trăm cacao nguyên chất">
          <strong>100%</strong>
          <span>Cacao nguyên chất</span>
          <small>Chọn lọc tự nhiên</small>
        </div>
        <div className="hero-carousel-dots" aria-hidden="true">
          <span className="is-active" />
          <span />
          <span />
          <span />
        </div>
      </div>
    </section>
  );
}
